import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { SectionTitle } from '@/features/app-shell/section'
import { collectorQueries } from '@/features/integrations/collectors/queries'
import { NewIntegrationListSkeleton } from '@/features/integrations/components/new-integration/skeleton'
import { getNetworkLogsEnabled } from '@/features/integrations/network-logs-allowlist'
import { integrationQueries } from '@/features/integrations/queries'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import BackLink from '@/components/common/back-link'

const NewIntegrationList = lazy(
	() => import('@/features/integrations/components/new-integration/list'),
)

/**
 * Catalog view state, persisted for shareable/back-button-friendly URLs:
 * - `q` — the search text (URL-synced like the data-table search, so a link with
 *   `?q=okta` opens pre-filtered).
 * - `category` — the active rail selection, a display-group slug derived from the
 *   group label (no hand-named map).
 * - `type` — the active Cloud/Network Logs tab, an integration-type slug
 *   (`cloud` | `network-log`). Absent = Cloud (the default; the tab bar only
 *   appears when the tenant's plan offers device families).
 * - `collector` — the Collector a Network Logs card opens the Add Log Source
 *   wizard on. Set by the surfaces already about one Collector; absent, the card
 *   picks a default (`defaultCollectorIdFor`).
 *
 * All four are validated only as strings; an unknown category/type slug
 * resolves to its default at render, so a stale/renamed slug degrades gracefully.
 * An unknown collector id reaches the wizard, which renders its not-found state.
 */
const newIntegrationSearchSchema = z.object({
	q: z.string().optional(),
	category: z.string().optional(),
	type: z.string().optional(),
	collector: z.string().optional(),
})

export const Route = createFileRoute('/_dashboard/integrations/new/')({
	validateSearch: zodValidator(newIntegrationSearchSchema),

	beforeLoad: () => roleAuthGuard(ROLES.SystemManager),

	loader: ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		if (tenant) {
			queryClient.prefetchQuery(integrationQueries.available(tenant))
			// The already-connected match joins the catalog against the
			// tenant's connectors, so prefetch the list alongside the catalog.
			queryClient.prefetchQuery(integrationQueries.list(tenant))
			queryClient.prefetchQuery(integrationQueries.microsoftConsentURL(tenant))
			// The device families ARE the Network Logs tab (the page suspends on
			// them), and a card with no `?collector=` picks its default Collector
			// from the tenant's Collectors, so both reads start here beside the
			// catalog rather than after it. A tenant outside the network-logs
			// allowlist has no such tab, so neither read is started for it.
			if (getNetworkLogsEnabled()) {
				queryClient.prefetchQuery(collectorQueries.summaryList(tenant))
				queryClient.prefetchQuery(collectorQueries.logSourceOptions(tenant))
			}
		}
	},

	head: () => ({
		meta: [
			{
				title: 'New Integration | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: NewIntegrationListSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	return (
		<>
			<SectionTitle className="flex items-center gap-4">
				<BackLink to="/integrations" />
				New Integration
			</SectionTitle>

			<p className="text-muted-foreground -mt-2 mb-4">
				Find the integration you want to add. AIR® guides you into the right
				setup for its connection type.
			</p>

			<Suspense fallback={<NewIntegrationListSkeleton />}>
				<NewIntegrationList />
			</Suspense>
		</>
	)
}
